import type { FC } from 'react';
import { useState } from 'react';
import type { ViewStyle } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import type { SharedValue } from 'react-native-reanimated';
import { useAnimatedReaction } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { circleSize } from '@/shared/utils/circle';

const DOT_SIZE = 8;

const styles = StyleSheet.create({
  container: {
    gap: 5,
    marginVertical: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dot: {
    borderWidth: 1,
    ...circleSize(DOT_SIZE)
  }
});

type Props = {
  color: string;
  size: number;
  progress: SharedValue<number>;
  style?: ViewStyle;
  onPress?: (index: number) => void;
};

export const DotPagination: FC<Props> = ({
  color,
  size,
  progress,
  style,
  onPress
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useAnimatedReaction(
    () => progress.value,
    (value) => {
      scheduleOnRN(setActiveIndex, Math.round(value));
    }
  );

  if (size <= 1) return;

  return (
    <View style={[styles.container, style]}>
      {Array.from({ length: size }, (_, i) => i).map((index) => {
        const isActive = index === activeIndex;
        return (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`carousel-page-${index}`}
            accessibilityHint={`carousel-page-${index}`}
            key={index}
            onPress={() => onPress?.(index)}
            hitSlop={8}
          >
            <View
              style={[
                styles.dot,
                isActive
                  ? { backgroundColor: color, borderColor: color }
                  : { backgroundColor: 'transparent', borderColor: color }
              ]}
            />
          </Pressable>
        );
      })}
    </View>
  );
};
